/**
 * <s-cursor>: on mouse devices, the pointer over any [data-cursor] element inside
 * this element's parent becomes a brand-gradient circle with an arrow that eases
 * after the mouse. Press tilts the arrow; release shoots it right and back. The
 * native cursor hides only once this runs. Off with reduced motion and touch.
 */
class SCursor extends HTMLElement {
  #scope = null;
  #frame = 0;
  #x = 0; #y = 0; #tx = 0; #ty = 0;
  #onMove = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#tx = event.clientX;
    this.#ty = event.clientY;
    const over = event.target.closest?.('[data-cursor]');
    if (over && !this.hasAttribute('data-on')) { this.#x = this.#tx; this.#y = this.#ty; }
    this.toggleAttribute('data-on', Boolean(over));
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#tick);
  };
  #onLeave = () => this.removeAttribute('data-on');
  #onDown = () => this.setAttribute('data-press', '');
  #onUp = () => {
    if (!this.hasAttribute('data-press')) return;
    this.removeAttribute('data-press');
    this.setAttribute('data-shoot', '');
    setTimeout(() => this.removeAttribute('data-shoot'), 420);
  };
  #tick = () => {
    this.#x += (this.#tx - this.#x) * 0.2;
    this.#y += (this.#ty - this.#y) * 0.2;
    this.style.translate = `${this.#x.toFixed(1)}px ${this.#y.toFixed(1)}px`;
    const settled = Math.abs(this.#tx - this.#x) < 0.3 && Math.abs(this.#ty - this.#y) < 0.3;
    this.#frame = settled ? 0 : requestAnimationFrame(this.#tick);
  };

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.#scope = this.parentElement;
    if (!this.#scope) return;
    this.#scope.setAttribute('data-cursor-on', '');
    this.#scope.addEventListener('pointermove', this.#onMove, { passive: true });
    this.#scope.addEventListener('pointerleave', this.#onLeave);
    this.#scope.addEventListener('pointerdown', this.#onDown);
    window.addEventListener('pointerup', this.#onUp);
  }

  disconnectedCallback() {
    this.#scope?.removeAttribute('data-cursor-on');
    this.#scope?.removeEventListener('pointermove', this.#onMove);
    this.#scope?.removeEventListener('pointerleave', this.#onLeave);
    this.#scope?.removeEventListener('pointerdown', this.#onDown);
    window.removeEventListener('pointerup', this.#onUp);
    cancelAnimationFrame(this.#frame);
  }
}

if (!customElements.get('s-cursor')) customElements.define('s-cursor', SCursor);
